import Navigation from "@navigation/index";
import firebase from "@services/firebase";
import { useAuthStore } from "@store/auth";
import { useDataStore } from "@store/user_org";
import { doc, onSnapshot } from "firebase/firestore";
import { useEffect, useRef, useState } from "react";
import Gleap from "gleap";
import { signatureRequestsApi } from "@services/api/signatureRequests";
import { isGUEST } from "@helpers/accessHelpers";
import { isMobileDevice } from "@constants/mobileApp";
import type { SignatureRequest } from "types";

const isLocalhost = window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1";
const isProd = process.env.REACT_APP_ENV === "prod" && !isLocalhost;

type MaintenanceData = {
  active: boolean;
  message: string;
  estimated_end: string;
};

export default function App() {
  const user = useAuthStore((state) => state.user);
  const initAuthListener = useAuthStore((state) => state.initAuthListener);
  const isImpersonating = useAuthStore((state) => state.isImpersonating);
  const returnToAdmin = useAuthStore((state) => state.returnToAdmin);
  const fetchUserData = useDataStore((state) => state.fetchUserData);
  const clearAllData = useDataStore((state) => state.clearAllData);
  const orgData = useDataStore((state) => state.orgData);
  const userData = useDataStore((state) => state.userData);

  const [localVersion, setLocalVersion] = useState<number | null>(null);
  const [showRefreshBanner, setShowRefreshBanner] = useState(false);
  const [maintenanceData, setMaintenanceData] =
    useState<MaintenanceData | null>(null);
  const unsubscribeVersion = useRef<(() => void) | null>(null);
  const [pendingSignatures, setPendingSignatures] = useState<SignatureRequest[]>([]);
  const [dismissedPendingSignatures, setDismissedPendingSignatures] = useState(false);
  const isGuest = isGUEST(userData);

  const isDevOrg = orgData?.id === "ur1LXurQw1Fv3KBy8XDI";
  const isMaintenanceProof = userData?.config?.maintenance_proof === true;

  // Demo mode: hide MBMO branding from the browser tab (title + favicon).
  useEffect(() => {
    document.title = isDevOrg ? "Pilot" : "MBMO";
    const favicon = document.querySelector<HTMLLinkElement>(
      "link[rel='icon']"
    );
    if (favicon) {
      favicon.href = isDevOrg
        ? "data:image/svg+xml,%3Csvg%20xmlns='http://www.w3.org/2000/svg'/%3E"
        : "/mbmo_app_logo.png";
    }
  }, [isDevOrg]);

  // Initialize Gleap.
  useEffect(() => {
    Gleap.initialize("c9NrqEY7tsg0dhTFIiM4Fe25jNP7RmGn");
    // Hide the Gleap feedback bubble on mobile web — it overlaps the UI and
    // collides with the "ouvrir dans l'application" nudges. Desktop keeps it.
    if (isMobileDevice()) {
      Gleap.showFeedbackButton(false);
    }
  }, []);

  // Identify user in Gleap
  useEffect(() => {
    if (userData) {
      Gleap.identify(userData.id, {
        name: userData.infos.full_name || userData.infos.display_name,
        email: userData.infos.email,
        phone: userData.infos.phone_number,
        companyId: userData.org_id,
        companyName: orgData?.infos?.name,
      });
    } else {
      Gleap.clearIdentity();
    }
  }, [userData, orgData]);

  // Initialize auth listener on mount
  useEffect(() => {
    const unsubscribe = initAuthListener();
    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, []);

  // Fetch user data when user logs in
  useEffect(() => {
    if (user?.uid) {
      fetchUserData(user.uid);
    } else if (user === null) {
      clearAllData();
    }
  }, [user?.uid]);

  // Poll pending signatures for guests, surface a global banner.
  useEffect(() => {
    if (!user?.uid || !isGuest) {
      setPendingSignatures([]);
      return;
    }
    let cancelled = false;
    const knownIds = new Set<string>();
    const fetchPending = async () => {
      try {
        const reqs = await signatureRequestsApi.listPendingForMe();
        if (cancelled) return;
        const incomingIds = reqs.map((r) => r.id);
        const hasNew = incomingIds.some((id) => !knownIds.has(id));
        knownIds.clear();
        incomingIds.forEach((id) => knownIds.add(id));
        setPendingSignatures(reqs);
        if (hasNew) setDismissedPendingSignatures(false);
      } catch {
        // Non-critical
      }
    };
    fetchPending();
    const interval = setInterval(fetchPending, 60 * 1000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [user?.uid, isGuest]);

  // Fetch local version baked into the build
  useEffect(() => {
    fetch("/version.txt")
      .then((res) => res.text())
      .then((text) => setLocalVersion(parseInt(text.trim(), 10)))
      .catch(() => {});
  }, []);

  // Listen to Firestore version — show banner when a new deployment is available
  useEffect(() => {
    if (localVersion === null) return;

    if (unsubscribeVersion.current) unsubscribeVersion.current();

    const versionRef = doc(firebase.db, "maintenance", "version");
    unsubscribeVersion.current = onSnapshot(versionRef, (snap) => {
      if (!snap.exists()) return;
      const remoteVersion: number = snap.data().react_version;
      if (isProd && remoteVersion > localVersion) {
        setShowRefreshBanner(true);
      }
    });

    return () => {
      if (unsubscribeVersion.current) unsubscribeVersion.current();
    };
  }, [localVersion]);

  // Poll maintenance status via API (public endpoint, no auth needed)
  useEffect(() => {
    const checkMaintenance = async () => {
      try {
        const { getMaintenanceStatus } =
          await import("@services/api/maintenance");
        const data = await getMaintenanceStatus();
        setMaintenanceData(data);
      } catch {
        // API not reachable — ignore
      }
    };

    checkMaintenance();
    const interval = setInterval(checkMaintenance, 60_000);
    return () => clearInterval(interval);
  }, [user]);

  const showMaintenance =
    maintenanceData?.active === true && !isDevOrg && !isMaintenanceProof;

  const formatEstimatedEnd = (iso: string) => {
    try {
      const date = new Date(iso);
      return date.toLocaleString("fr-FR", {
        weekday: "long",
        day: "numeric",
        month: "long",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return iso;
    }
  };

  if (showMaintenance) {
    return (
      <div className="fixed inset-0 z-[9999] bg-neutral-50 flex items-center justify-center">
        <div className="flex flex-col items-center gap-8 max-w-md text-center px-6">
          <div className="flex flex-col items-center gap-3">
            <h1 className="text-xl font-semibold text-neutral-900">
              Maintenance en cours
            </h1>
            <p className="text-neutral-500 text-sm leading-relaxed">
              {maintenanceData.message}
            </p>
          </div>

          <div className="w-64 h-1.5 bg-neutral-200 rounded-full overflow-hidden">
            <div
              className="h-full bg-blue-400 shadow-lg shadow-blue-300 rounded-full"
              style={{
                animation: "maintenance-progress 2.5s ease-in-out infinite",
              }}
            />
          </div>

          {maintenanceData.estimated_end && (
            <p className="text-neutral-400 text-xs">
              Fin estimée : {formatEstimatedEnd(maintenanceData.estimated_end)}
            </p>
          )}

          <button
            onClick={() => useAuthStore.getState().logout()}
            className="mt-4 text-sm text-neutral-400 hover:text-neutral-600 underline transition-colors"
          >
            Se déconnecter
          </button>
        </div>

        <style>{`
          @keyframes maintenance-progress {
            0% { width: 0%; margin-left: 0; }
            50% { width: 60%; margin-left: 20%; }
            100% { width: 0%; margin-left: 100%; }
          }
        `}</style>
      </div>
    );
  }

  return (
    <>
      <Navigation />
      {isImpersonating && (
        <div
          onClick={async () => {
            clearAllData();
            await returnToAdmin();
          }}
          className="fixed top-0 left-0 right-0 z-50 cursor-pointer bg-amber-500 text-white text-center py-1 text-h4 font-medium hover:bg-amber-600 transition-colors"
        >
          Connecté en tant que {userData?.infos.full_name || userData?.infos.display_name || userData?.infos.email} — Cliquez pour revenir au compte admin
        </div>
      )}
      {isGuest && pendingSignatures.length > 0 && !dismissedPendingSignatures && (
        <div className="fixed top-10 left-1/2 -translate-x-1/2 z-50 flex items-center gap-3 px-6 py-3 bg-neutral-900 rounded-xl shadow-lg text-white">
          <div
            className="cursor-pointer flex flex-col"
            onClick={() => {
              const first = pendingSignatures[0];
              if (!first) return;
              window.location.href = `/project/${first.project_id}?tab=documents&sub=signatures&sig=${first.id}`;
            }}
          >
            <div className="font-semibold text-h2">
              {pendingSignatures.length > 1
                ? `${pendingSignatures.length} documents en attente de signature.`
                : "Un document est en attente de signature."}
            </div>
            <div className="text-h3 text-neutral-400">
              Cliquez pour signer.
            </div>
          </div>
          <button
            onClick={() => setDismissedPendingSignatures(true)}
            className="text-neutral-400 hover:text-white text-2xl leading-none px-2"
            aria-label="Fermer"
          >
            ×
          </button>
        </div>
      )}
      {showRefreshBanner && isProd && (
        <div
          onClick={() => window.location.reload()}
          className="fixed bottom-10 left-1/2 -translate-x-1/2 z-50 cursor-pointer hover:-translate-y-1 hover:scale-105 transition-all duration-300"
        >
          <div className="px-8 py-4 bg-neutral-900 rounded-xl shadow-lg flex flex-col gap-1 text-white">
            <div className="font-semibold text-h1">
              Une nouvelle version est disponible.
            </div>
            <div className="text-h2 text-neutral-400">
              Cliquez ici pour mettre à jour.
            </div>
          </div>
        </div>
      )}
    </>
  );
}
